// 고객관리 화면 열기 묶음 (2026-08-11, task 472)
//
// 고객현황 화면은 열릴 때 단계별 건수·상태별 건수·대시보드 메모·카톡 후보를 **각각 따로**
// 요청했다. 신청서 목록·기준값까지 합치면 한 번에 최대 8개인데, 브라우저가 한 호스트에 동시에
// 여는 연결은 6개까지다. 게다가 DB 통로는 다른 화면과 함께 쓰므로 누가 통로를 물면 이 화면도
// 같이 대기줄에 선다 — 2026-08-11 문자함이 통로 4칸을 독점했을 때 실제로 그랬다.
//
// 그래서 **"같은 순간에 몰린 조회"만** `/crm/bootstrap` 하나로 보낸다. 혼자 오는 조회
// (메모 저장 뒤 목록만 다시 읽기, 카톡 후보 30초 폴링)는 그대로 자기 라우트로 간다 — 안 그러면
// 한 항목 갱신이 네 항목 재조회로 부풀어, 줄이려던 부하가 오히려 늘어난다.
//
// 화면 코드와 react-query 키는 하나도 안 바뀐다. 묶는 일은 이 계층 안에서만 일어난다.
// (시세표 `policyApi.ts` 의 bootstrapSlice 와 같은 구조 — 그쪽이 먼저 검증된 원본이다.)

import type { KakaoWorkCandidateList } from "../../shared/kakao-work-candidates";
import type { DashboardMemo } from "./memos";
import type { PipelineCounts, StageCount } from "./crm";
import { authJson } from "./_client";

const API = `${import.meta.env.VITE_API_BASE_URL ?? ""}/api/v2/crm`;

type CrmBootstrapPayload = {
  stageCounts: { stageCounts: StageCount[] };
  pipelineCounts: PipelineCounts;
  memos: { memos: DashboardMemo[] };
  /** 권한 판정이 실패하면 서버가 null 을 준다 — 그때는 이 조각만 자기 경로로 다시 읽는다. */
  kakaoWorkCandidates: KakaoWorkCandidateList | null;
};

/** 몰림을 모으는 창. 화면 하나가 여러 조회를 띄우는 데 걸리는 몇 ms 를 덮을 만큼만 짧게. */
const BOOTSTRAP_BATCH_MS = 12;

type BootstrapWaiter = {
  slice: keyof CrmBootstrapPayload;
  resolve: (value: never) => void;
  reject: (error: unknown) => void;
  /** 묶지 않을 때(혼자 왔거나 묶음이 실패했을 때) 쓰는 원래 경로. */
  alone: () => Promise<unknown>;
};

let bootstrapBatch: BootstrapWaiter[] | null = null;

function flushBootstrapBatch(): void {
  const waiters = bootstrapBatch;
  bootstrapBatch = null;
  if (!waiters || waiters.length === 0) return;

  const runAlone = (waiter: BootstrapWaiter, fallbackError?: unknown) =>
    waiter.alone().then(
      (value) => waiter.resolve(value as never),
      (error) => waiter.reject(fallbackError ?? error),
    );
  // 혼자 왔으면 묶을 이유가 없다 — 자기 것만 읽는다.
  if (waiters.length === 1) {
    void runAlone(waiters[0]);
    return;
  }

  // ⚠️ **이 순간 실제로 기다리는 조각만** 서버에 청구한다. 화면마다 필요한 조각이 다르기 때문이다
  // — 고객현황은 상태 건수·메모·카톡 후보 셋만 쓰고 단계 건수는 안 쓴다. 늘 넷을 다 계산하면
  // 줄이려던 DB 일이 오히려 늘어난다(시세표 묶음은 다섯 조각을 한 화면이 전부 써서 이 문제가 없었다).
  const slices = [...new Set(waiters.map((w) => w.slice))];
  void authJson<Partial<CrmBootstrapPayload>>(
    `${API}/bootstrap?slices=${encodeURIComponent(slices.join(","))}`,
  ).then(
    (payload) => {
      for (const waiter of waiters) {
        const value = payload[waiter.slice];
        // 묶음이 그 조각을 못 채웠으면(카톡 후보 권한 판정 실패) 그 조각만 원래 경로로.
        if (value == null) void runAlone(waiter);
        else waiter.resolve(value as never);
      }
    },
    // 묶음 라우트가 없는 서버(배포 롤아웃 중 옛 태스크)에서도 화면이 살아야 한다 → 각자 원래 경로로.
    (error) => {
      for (const waiter of waiters) void runAlone(waiter, error);
    },
  );
}

function bootstrapSlice<K extends keyof CrmBootstrapPayload>(
  slice: K,
  alone: () => Promise<NonNullable<CrmBootstrapPayload[K]>>,
): Promise<NonNullable<CrmBootstrapPayload[K]>> {
  return new Promise<NonNullable<CrmBootstrapPayload[K]>>((resolve, reject) => {
    if (!bootstrapBatch) {
      bootstrapBatch = [];
      setTimeout(flushBootstrapBatch, BOOTSTRAP_BATCH_MS);
    }
    bootstrapBatch.push({
      slice,
      resolve: resolve as (value: never) => void,
      reject,
      alone: alone as () => Promise<unknown>,
    });
  });
}

/** 단계별 신청서 건수. 같은 순간에 다른 조회가 함께 오면 묶음으로 간다. */
export async function fetchStageCountsBatched(
  alone: () => Promise<{ stageCounts: StageCount[] }>,
): Promise<StageCount[]> {
  const { stageCounts } = await bootstrapSlice("stageCounts", alone);
  return stageCounts;
}

/** 상태별 신청서 건수(현황판 카드). */
export function fetchPipelineCountsBatched(
  alone: () => Promise<PipelineCounts>,
): Promise<PipelineCounts> {
  return bootstrapSlice("pipelineCounts", alone);
}

/** 대시보드 메모 — 활성 목록만. 보관함(archived=1)은 사람이 열었을 때만 나가므로 묶지 않는다. */
export async function fetchMemosBatched(
  alone: () => Promise<{ memos: DashboardMemo[] }>,
): Promise<DashboardMemo[]> {
  const { memos } = await bootstrapSlice("memos", alone);
  return memos;
}

/** 카톡 후보 — 기본 기간(7일)만. 1·30일은 사람이 고른 뒤라 혼자 나간다. */
export function fetchKakaoWorkCandidatesBatched(
  alone: () => Promise<KakaoWorkCandidateList>,
): Promise<KakaoWorkCandidateList> {
  return bootstrapSlice("kakaoWorkCandidates", alone);
}
